import { ICommandArgs, useRegisterCommands } from "~/environment/command.service";
import { RefObject, useCallback, useRef, useState } from "react";
import { IListRef } from "~/components/list";
import { showNotImplementedToastMsg } from "~/environment/toast-service";
import {
  markDoneCommand,
  markNotDoneCommand,
  setThreadReminderCommand,
  removeThreadReminderCommand,
  starThreadCommand,
  unstarThreadCommand,
  getCommandFactory,
} from "~/utils/common-commands";
import { ISearchEditorProps, ISearchEditorRef } from "~/components/forms/search-editor";
import { IFormControl } from "solid-forms-react";
import { useClientEnvironment } from "~/environment/ClientEnvironmentContext";
import { useControlState } from "~/components/forms/utils";
import { useAuthGuardContext } from "~/route-guards/withAuthGuard";
import { PointerWithRecord } from "libs/schema";
import { SearchMessagesParams } from "libs/database";
import { ClientEnvironment } from "~/environment/ClientEnvironment";
import { SetOptional } from "type-fest";
import { useAsRef } from "~/hooks/useAsRef";
import { ClientApiRequestOptions } from "~/environment/api.service";
import { AbortError } from "libs/errors";
import { useUnmount } from "react-use";
import { isAbortError } from "libs/predicates";
import { ApiOutput } from "libs/ApiTypes";
import { isSuggestionDialogOpened } from "~/components/forms/tiptap/suggestion-utils";

/* -------------------------------------------------------------------------------------------------
 * useRegisterSearchViewCommands
 * -----------------------------------------------------------------------------------------------*/

export function useRegisterSearchViewCommands(props: {
  editorRef: RefObject<ISearchEditorRef>;
  searchEditorRef: RefObject<HTMLDivElement>;
  searchControl: ISearchEditorProps["control"];
  search: () => Promise<void>;
  onlySearchSeenPostsControl: IFormControl<boolean>;
}) {
  const { editorRef, searchControl, search, onlySearchSeenPostsControl, searchEditorRef } = props;

  const { currentUser } = useAuthGuardContext();
  const environment = useClientEnvironment();

  useRegisterCommands({
    commands: () => {
      return [
        performSearchCommand({
          callback: (event) => {
            if (event && !searchEditorRef.current?.contains(event.target as Node)) {
              return false;
            }

            if (isSuggestionDialogOpened()) return false;

            search();
            return true;
          },
        }),
        {
          label: "Go to Search",
          hotkeys: ["/", "Shift+/"],
          callback: () => {
            editorRef.current?.focus("all");
          },
        },
        {
          label: "Select query or Go to Inbox",
          showInKBar: false,
          triggerHotkeysWhenInputFocused: true,
          hotkeys: ["Escape"],
          callback: () => {
            const editor = editorRef.current?.editor;
            if (!editor) return false;

            if (!editor.isFocused) {
              editor.commands.focus("all");
            } else if (isSuggestionDialogOpened()) {
              return false;
            } else {
              environment.router.navigate("/inbox");
            }
          },
        },
        markDoneCommand({
          callback: () => {
            showNotImplementedToastMsg(`
              Unfortunately, you can't mark threads Done from the
              Search page.
            `);
          },
        }),
        markNotDoneCommand({
          callback: () => {
            showNotImplementedToastMsg(`
              Unfortunately, you can't mark threads not Done from the
              Search page.
            `);
          },
        }),
        setThreadReminderCommand({
          callback: () => {
            showNotImplementedToastMsg(`
              Unfortunately, you can't edit reminders from the
              Search page.
            `);
          },
        }),
        removeThreadReminderCommand({
          callback: () => {
            showNotImplementedToastMsg(`
              Unfortunately, you can't edit reminders from the
              Search page.
            `);
          },
        }),
        starThreadCommand({
          callback: () => {
            showNotImplementedToastMsg(`
              Unfortunately, you can't star threads from the
              Search page.
            `);
          },
        }),
        unstarThreadCommand({
          callback: () => {
            showNotImplementedToastMsg(`
              Unfortunately, you can't unstar threads from the
              Search page.
            `);
          },
        }),
      ];
    },
    deps: [editorRef, searchControl, currentUser.id, environment, search, onlySearchSeenPostsControl],
  });
}

export const performSearchCommand = getCommandFactory(
  "PERFORM_SEARCH",
  (options: SetOptional<ICommandArgs, "label" | "triggerHotkeysWhenInputFocused" | "hotkeys">): ICommandArgs => ({
    label: "Submit",
    hotkeys: ["Enter", "$mod+Enter"],
    triggerHotkeysWhenInputFocused: true,
    ...options,
  }),
);

export function usePerformSearch(props: {
  searchControl: ISearchEditorProps["control"];
  resultsListRef: RefObject<IListRef<PointerWithRecord<"message">>>;
  onlySearchSeenPostsControl: IFormControl<boolean>;
}) {
  const { searchControl, onlySearchSeenPostsControl, resultsListRef } = props;

  const [searchResults, setSearchResults] = useState<ApiOutput<"search">["hits"]>([]);

  const [searchLimit, setSearchLimit] = useState(initialSearchLimit);
  const [isEndOfSearch, setIsEndOfSearch] = useState(true);
  const { currentUser } = useAuthGuardContext();
  const currentUserId = currentUser.id;
  const environment = useClientEnvironment();

  const onlySearchSeenPosts = useControlState(() => onlySearchSeenPostsControl.value, [onlySearchSeenPostsControl]);
  const isSearchPending = useControlState(() => searchControl.isPending, [searchControl]);

  const searchLimitRef = useAsRef(searchLimit);

  const abortControllerRef = useRef<AbortController | null>(null);

  const abortSearch = useCallback(() => {
    abortControllerRef.current?.abort(new AbortError());
    abortControllerRef.current = null;
  }, [abortControllerRef]);

  // Abort an existing search if we navigate away from the search page
  useUnmount(abortSearch);

  const search = useCallback(
    async (props: { isFetchMore?: boolean } = {}) => {
      // Abort any existing search
      abortSearch();

      if (!searchControl.isValid) {
        environment.logger.debug("[search] Search is invalid. Ignoring...");
        return;
      }

      const query = searchControl.rawValue.queryText;

      if (!query) {
        setSearchResults([]);
        setSearchLimit(initialSearchLimit);
        setIsEndOfSearch(true);
        updateNavigationParams(environment, query);
        return;
      }

      searchControl.markPending(true);
      abortControllerRef.current = new AbortController();

      if (!props.isFetchMore) {
        setSearchResults([]);
        setSearchLimit(initialSearchLimit);
      }

      /*
      This `setTimeout` is needed because when we call the setState functions, the update state value is not available
      immediately. So if you call `setSearchLimit` and then read `searchLimit` immediately afterwards, you will get the
      old value. The main issue this causes is related to pagination where we need to wait before reading `searchLimit`
      after calling `setSearchLimit`. This `setTimeout` adds that wait.

      There are better solutions than this as `setTimeout` doesn't guarantee that the state value has been updated. But
      it works and it's much simpler than the alternatives.
       */
      setTimeout(async () => {
        try {
          const startTime = Date.now();

          const result = await performSearch(
            {
              query: query + (onlySearchSeenPosts ? " is:seen" : ""),
              currentUserId: currentUserId,
              environment,
              limit: searchLimitRef.current,
            },
            { abortSignal: abortControllerRef.current?.signal },
          );

          // search is successful so we don't need the abortController anymore
          abortControllerRef.current = null;

          updateNavigationParams(environment, query);

          setSearchResults(result.messages);

          setIsEndOfSearch(result.messages.length < searchLimitRef.current);

          if (result.messages.length) {
            resultsListRef.current?.focus();
          }

          /*
          The searchControl's pending state is being brought into React via `useControlState`
          which itself uses `useSyncExternalStore` internally. When rendering, React always grabs
          the current state value of an external store using the provided selector. As a consequence,
          when rendering react always shows the latest external store values, but native react state
          might take a tick to be reflected. To ensure that we don't mark this query as done pending
          before we've updated the searchResults state, we need to wait a tick before marking the
          searchControl as done pending.
           */
          setTimeout(() => searchControl.markPending(false));

          environment.logger.debug(`[search] Search took ${Date.now() - startTime}ms`);
        } catch (error) {
          if (!isAbortError(error)) {
            environment.logger.error({ error }, `[search] Error performing search`);
          }

          // See comment above for why we're wrapping this in a setTimeout
          setTimeout(() => searchControl.markPending(false));
        }
      });
    },
    [abortSearch, searchControl, currentUserId, environment, searchLimitRef, resultsListRef],
  );

  const fetchMore = useCallback(async () => {
    setSearchLimit((prev) => prev + 100);
    search({ isFetchMore: true });
  }, [search]);

  return {
    onlySearchSeenPosts,
    search,
    abortSearch,
    fetchMore,
    searchResults,
    isEndOfSearch,
    isSearchPending,
  };
}

const initialSearchLimit = 100;

const performSearch = async (
  props: {
    query: string;
    currentUserId: string;
    environment: Pick<ClientEnvironment, "api" | "writeRecordMap" | "isLoading">;
    limit: number;
  },
  options?: ClientApiRequestOptions,
): Promise<{
  messages: ApiOutput<"search">["hits"];
  processingTimeMs?: number;
  estimatedTotalHits?: number;
}> => {
  const { query, currentUserId, environment, limit } = props;

  const params: SearchMessagesParams = {
    query,
    currentUserId,
    limit,
  };

  using disposable = environment.isLoading.add();

  const response = await environment.api.search(params, options);

  if (response.status === 200 && response.body.recordMap) {
    environment.writeRecordMap(response.body.recordMap);

    return {
      messages: response.body.hits,
      processingTimeMs: response.body.processingTimeMs,
      estimatedTotalHits: response.body.estimatedTotalHits,
    };
  } else {
    return { messages: [] };
  }
};

function updateNavigationParams(environment: Pick<ClientEnvironment, "router">, query: string) {
  const currentLocation = environment.router.location();
  // Check to see if we're currently viewing a specific thread result
  const match = currentLocation.pathname.match(/search\/[^\/]+\/threads\/([^\/]+)/);

  let newPathname: string;

  if (match && query) {
    const threadId = match[1];
    newPathname = `/search/${encodeURIComponent(query)}/threads/${threadId}`;
  } else if (query) {
    newPathname = `/search/${encodeURIComponent(query)}`;
  } else {
    newPathname = `/search`;
  }

  if (currentLocation.pathname === newPathname) return;

  return environment.router.navigate(newPathname, {
    replace: true,
  });
}
